// © Microsoft Corporation. All rights reserved.

import { AppError } from "@msnews/diagnostics";
import { getElementTrackingDecorator } from "./PrimeElementTrackingDecorator";
import { getTrack } from "./PrimeTrack";
import { getTrackExtCompleteCanary } from "./PrimeTrackExtensionComplete";
import { getTrackInfo } from "./PrimeTrackInfo";

type trackingEventCall = {event: Event, element: HTMLElement, destination: string, headline: string};
type trackPartialImpression  = {elem?: HTMLElement, visibilityCheck?: boolean, mechanism?: TrackMechanism, topLevelContainerType?: string};
type trackingDecoratorCall  = {elementTree: HTMLElement, parentElement?: HTMLElement, options?: any, disableImpressionUpdate?: boolean};

let trackingResolved = false;
let trackingObject: PrimeGlobal.ITrack = null;
let elementTrackingDecorator: PrimeGlobal.IElementTrackingDecorator = null;
let trackExtCompleted: any = null;
let trackEventBuffer: trackingEventCall[] = [];
let trackImpressionBuffer: trackPartialImpression[] = [];
let trackAppErrorEventBuffer: AppError[] = [];
let elementTrackingDecoratorBuffer: trackingDecoratorCall[] = [];
let trackingPromiseCalled = false;

// Following code is to decouple Prime dependency from initial state of experience. Once tracking is ported to Peregrine, it would not be needed.
/**
 * Sets the tracking attributes on the given element(s) (e.g. data-m={'i':10, 'o':6, 'p':2} data-id=10)
 * If any elements contain 'i', 'i' will not change.
 * Ordinal and parent will sync to correct values for each element.
 * Will send an impression update for the new elements when called.
 * @param {HTMLElement} elementTree The complete element tree to decorate
 * @param {HTMLElement} parentElement The parent of the given element(s)
 * @param {any} options The settings for this call.
 * options.defaults [optional] default attributes for any particular object as a JS TelemetryItem object.
 * options.elementOverride [Optional] A callback in the form of (element, trackingAttributes) =&rt;void.
 * The callback can be used to alter or change any of the attributes before they are applied onto the element.
 * options.visibilityCheck [Optional] Flag indicating whether element visibility checking should be performed or not. By default is true.
 * options.selector [Optional] The selector to use for determining elements to instrument.
 * @param {boolean} disableImpressionUpdate Send impression update only when it is not disabled, by default it is false
 * @HTMLElement
 */
function setTrackingForSingleElement(elementTree: HTMLElement, parentElement?: HTMLElement, options?: any, disableImpressionUpdate?: boolean): HTMLElement {
    return elementTrackingDecorator.setTrackingForSingleElement(elementTree, parentElement, options, disableImpressionUpdate);
}

// Following code is to decouple Prime dependency from initial state of experience. Once tracking is ported to Peregrine, it would not be needed.
/**
 * Send the tracking call
 * @param {Event} event event object
 * @param {HTMLElement} element form code
 * @param {string} destination text
 * @param {string} headline text
 * @void
 */
function sendTrackingCall(event: Event, element: HTMLElement, destination: string, headline: string): void {
    trackingObject.trackEvent(event, element, destination, headline);
}

/**
 * Makes the partial page request
 * @param {HTMLElement} elem object
 * @param {boolean} visibilityCheck
 * @param {TrackMechanism} mechanism
 * @param {string} topLevelContainerType
 * @void
 */
function sendPartialPageRequest(elem?: HTMLElement, visibilityCheck?: boolean, mechanism?: TrackMechanism, topLevelContainerType?: string): void {
    trackingObject.trackPartialImpression(elem, visibilityCheck, mechanism, topLevelContainerType);
}

/**
 * Converts the AppError object to the Prime AppError type and make the trackAppErrorEvent call
 * @param appError - App error object
 */
function sendTrackAppErrorEvent(appError: AppError): void {
    trackingObject.trackAppErrorEvent({ errId: appError.id, errSource: appError.source, errMsg: appError.message });
}

/**
 * Processes the pending tracking and page impresssion calls for statics.
 * Dependencies: getTrack(), getTrackExtCompleteCanary(), getElementTrackingDecorator()
 * @void
 */
function processPendingTrackingCall(): void {
    Promise.all([getTrack(), getTrackExtCompleteCanary(), getElementTrackingDecorator()])
        .then(modules => {
            [trackingObject, trackExtCompleted, elementTrackingDecorator] = modules;

            // at this point we will process any pending requests and resolve tracking
            trackingResolved = true;
            if (trackEventBuffer.length > 0) {
                trackEventBuffer.forEach(eventObj => {
                    try {
                        sendTrackingCall(eventObj.event, eventObj.element, eventObj.destination, eventObj.headline);
                    } catch {
                    // This catch block is meant to continue to next element in the loop.
                    // We may remove this in future once Error Boundaries are added.
                    }
                });

                // empty it again after processing
                trackEventBuffer = [];
            }

            if (trackImpressionBuffer.length > 0) {
                trackImpressionBuffer.forEach(eventObj => {
                    sendPartialPageRequest(eventObj.elem, eventObj.visibilityCheck, eventObj.mechanism, eventObj.topLevelContainerType);
                });

                // empty it again after processing
                trackImpressionBuffer = [];
            }

            if (trackAppErrorEventBuffer.length > 0) {
                trackAppErrorEventBuffer.forEach(appError => {
                    sendTrackAppErrorEvent(appError);
                });

                // empty it again after processing
                trackAppErrorEventBuffer = [];
            }

            if (elementTrackingDecoratorBuffer.length > 0) {
                elementTrackingDecoratorBuffer.forEach(decorator => {
                    const {elementTree, parentElement, options, disableImpressionUpdate} = decorator;
                    setTrackingForSingleElement(elementTree, parentElement, options, disableImpressionUpdate);
                });

                // empty it again after processing
                elementTrackingDecoratorBuffer = [];
            }
        });
}

/**
 * Callback function to pass to experience. It maintains an internal array to hold track calls until dependencies resolved.
 * @param {HTMLElement} element form code
 * @param {string} action (Type of action. Necessary to make it compatible with 1DS Telemetry SDK)
 * @param {number} behavior (Type of beavior. Necessary to make it compatible with 1DS Telemetry SDK)
 * @param {Event} event event object
 * @param {string} destination text
 * @param {string} headline text
 * @void
 */
export function handleTelemetryCall(element: HTMLElement, action?: string, behavior?: number, event?: Event, destination?: string, headline?: string): void {
    if (trackingResolved) {
        sendTrackingCall(event, element, destination, headline);
    } else {
        trackEventBuffer.push({ event: event, element: element, destination: destination, headline: headline });

        if (!trackingPromiseCalled) {
            trackingPromiseCalled = true;

            // only want to call this once
            processPendingTrackingCall();
        }
    }
}

/**
 * Makes the partial page request
 * @param {HTMLElement} elem object
 * @param {} viewType (Necessary to make it compatible with 1DS Telemetry SDK)
 * @param {boolean} visibilityCheck
 * @param {TrackMechanism} mechanism
 * @param {string} topLevelContainerType
 * @void
 */
export function handlePartialPageImpressionCall(elem?: HTMLElement, viewType?, visibilityCheck?: boolean, mechanism?: TrackMechanism, topLevelContainerType?: string): void {
    if (trackingResolved) {
        sendPartialPageRequest(elem, visibilityCheck, mechanism, topLevelContainerType);
    } else {
        trackImpressionBuffer.push({ elem: elem, visibilityCheck: visibilityCheck, mechanism: mechanism, topLevelContainerType: topLevelContainerType });

        if (!trackingPromiseCalled) {
            trackingPromiseCalled = true;

            // only want to call this once
            processPendingTrackingCall();
        }
    }
}

/**
 * Handler to log app errors
 * @param appError - App error object
 * @void
 */
export function handleAppErrorEventCall(appError: AppError): void {
    if (trackingResolved) {
        sendTrackAppErrorEvent(appError);
    } else {
        trackAppErrorEventBuffer.push({ ...appError });
        if (!trackingPromiseCalled) {
            trackingPromiseCalled = true;

            // only want to call this once
            processPendingTrackingCall();
        }
    }
}

/**
 * Decorates prime telemetry tags
 * @param decorator - Decorator object
 * @void
 */
export function handleSetTrackingAttrs(elementTree: HTMLElement, parentElement?: HTMLElement, options?: any, disableImpressionUpdate?: boolean): void {
    if (trackingResolved) {
        setTrackingForSingleElement(elementTree, parentElement, options, disableImpressionUpdate);
    } else {
        elementTrackingDecoratorBuffer.push({ elementTree, parentElement, options, disableImpressionUpdate });
        if (!trackingPromiseCalled) {
            trackingPromiseCalled = true;

            // only want to call this once
            processPendingTrackingCall();
        }
    }
}